import { AlertCircle, Loader2 } from 'lucide-react'
import { apiErrorMessage, extractFieldErrors } from '@/lib/apiErrors'
import type { PublishResult } from '@/types/bundle'
import { bumpPatch } from '@/lib/version'
import { Banner } from './Banner'
import { InfoTooltip } from './InfoTooltip'
import { Modal } from './Modal'

export interface PublishModalProps {
  open: boolean
  liveVersion?: string
  releaseNote: string
  onReleaseNoteChange: (value: string) => void
  onConfirm: () => void
  onCancel: () => void
  onNavigateToDashboard: () => void
  isPending: boolean
  isError: boolean
  error: unknown
  isSuccess: boolean
  result?: PublishResult
}

// Frontend PRD 08 - completes the publish action Diff & Validation starts.
// Built on the generic Modal (not ConfirmModal, which is confirm/cancel-only
// with a hardcoded danger button - the wrong fit for a required release-note
// field and a non-destructive action) and kept pure/presentational per PRD
// 00's own component convention: every mutation/query lives in the caller
// (usePublishMutation), not in here.
export function PublishModal({
  open,
  liveVersion,
  releaseNote,
  onReleaseNoteChange,
  onConfirm,
  onCancel,
  onNavigateToDashboard,
  isPending,
  isError,
  error,
  isSuccess,
  result,
}: PublishModalProps) {
  const nextVersion = liveVersion ? bumpPatch(liveVersion) : '-'
  const publishErrors = isError ? extractFieldErrors(error) : undefined

  function handleClose() {
    if (!isPending) onCancel()
  }

  if (isSuccess && result) {
    return (
      <Modal open={open} title="Published" onClose={handleClose}>
        <div className="flex flex-col items-center gap-2 py-4 text-center">
          <span className="text-title font-semibold text-success">Version {result.version} has been published</span>
          <span className="text-body text-text-secondary">Use Go Live to make it the live version.</span>
          <button
            type="button"
            onClick={onNavigateToDashboard}
            className="mt-2 text-body text-accent hover:underline"
          >
            Back to Dashboard
          </button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      title="Publish new version"
      onClose={handleClose}
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded-control border border-border px-4 py-2 text-body font-medium text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending || releaseNote.trim() === ''}
            className="flex items-center gap-1.5 rounded-control bg-accent px-4 py-2 text-body font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPending && <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />}
            {isPending ? 'Publishing…' : 'Publish'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-meta text-text-secondary">
            New version
            <InfoTooltip text="Version numbers go up automatically each time you publish. This doesn't affect what's live until you use Go Live." />
          </span>
          <span className="text-header font-medium text-text-primary">{nextVersion}</span>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="release-note" className="flex items-center gap-1 text-body text-text-primary">
            Release note
            <InfoTooltip text="A short note describing what changed. It's saved with this version and shown in the Dashboard's recent activity." />
          </label>
          <textarea
            id="release-note"
            value={releaseNote}
            onChange={(event) => onReleaseNoteChange(event.target.value)}
            disabled={isPending}
            rows={4}
            className="rounded-control border border-border bg-surface px-3 py-2 text-body text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
            placeholder="What changed in this release?"
          />
        </div>

        {isError && (
          <Banner variant="danger" icon={AlertCircle} title={apiErrorMessage(error) ?? 'Publish failed'}>
            <div className="flex flex-col gap-2">
              <span>Your changes are still saved as a draft - try publishing again.</span>
              {publishErrors && publishErrors.length > 0 && (
                <div className="flex flex-col gap-1 rounded-control border border-danger/40 bg-danger/10 px-3 py-2">
                  {publishErrors.map((issue, index) => (
                    <span key={issue.path ?? index} className="text-meta text-danger">
                      {issue.message}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Banner>
        )}
      </div>
    </Modal>
  )
}
